import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { AppleSignInButton } from "@/components/AppleSignInButton";

export default function Register() {
  const { register, loginWithGoogle, loading } = useAuth();
  const navigate = useNavigate();
  const nextParam = new URLSearchParams(useLocation().search).get("next");
  const next = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : null;
  const [form, setForm] = useState({ full_name: "", email: "", password: "", date_of_birth: "" });
  const [age, setAge] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!age) {
      toast.error("Please confirm you are 18 or over.");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    try {
      await register({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,
        date_of_birth: form.date_of_birth,
        country: "GB",
        age_confirmed: true,
      });
      toast.success("Account created — welcome to Fable +");
      navigate(next || "/marketplace", { replace: true });
    } catch (err) {
      toast.error(err.message || "Could not create account");
    }
  };

  return (
    <div className="fable-mesh flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-xl">
        <div className="mb-6 text-center">
          <Logo className="text-3xl" />
          <h1 className="mt-4 font-serif-display text-2xl font-bold">Create your account</h1>
          <p className="mt-1 text-sm text-muted-foreground">One account, six worlds.</p>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full gap-2 rounded-full"
          onClick={loginWithGoogle}
          data-testid="google-signin"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
          Continue with Google
        </Button>
        <AppleSignInButton />

        <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" required value={form.full_name} onChange={set("full_name")} data-testid="register-name" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="register-email" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={form.password} onChange={set("password")} data-testid="register-password" className="mt-1.5" placeholder="At least 6 characters" />
          </div>
          <div>
            <Label htmlFor="dob">Date of birth</Label>
            <Input id="dob" type="date" required value={form.date_of_birth} onChange={set("date_of_birth")} data-testid="register-dob" className="mt-1.5" />
          </div>
          <label className="flex items-start gap-2.5 text-sm">
            <Checkbox checked={age} onCheckedChange={(v) => setAge(!!v)} data-testid="register-age" className="mt-0.5" />
            <span className="text-muted-foreground">I confirm I am 18 or over and agree to the Terms &amp; Privacy Policy.</span>
          </label>
          <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="register-submit">
            {loading ? "Creating…" : "Create account"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to={next ? "/login?next=" + encodeURIComponent(next) : "/login"} className="font-semibold text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
